import { Link } from "wouter";
import FaqAccordion from "@/components/FaqAccordion";
import ArabicToggle from "@/components/ArabicToggle";
import AudioPlayer from "@/components/AudioPlayer";
import { ArrowRight, Bot, Zap, Target, BarChart3, Users, Workflow, CheckCircle2 } from "lucide-react";
import Header from "@/components/Header";
import SEOHead from "@/components/SEOHead";
import SharePostRow from "@/components/SharePostRow";


import { trackSectionCtaClick, trackCalendlyClick } from "@/lib/analytics";
import SchemaScript from "@/components/SchemaScript";
const PAGE_TITLE = "Agentic AI Marketing Agency London | AI Lead Generation";
const PAGE_DESCRIPTION = "Agentic AI marketing agency in London: we build Gemini Enterprise multi-agent systems for B2B marketing and sales that draft content and support GTM.";
const FAQ_FIRST_ANSWER = "An agentic AI marketing agency builds AI agents that research accounts, draft content, monitor buying signals and prepare follow-ups inside your existing tools. Integrated.Social deploys them on Gemini Enterprise for B2B teams in London and the UK. Agents draft; a named senior person signs off anything buyer-facing, and no agent spends or publishes alone.";
const ANSWER_BLOCK = `${FAQ_FIRST_ANSWER} It suits stretched B2B marketing teams; start with a 30-minute call to choose one bounded workflow to test first.`;
const FAQ_SUMMARY = "These answers, across both FAQ sections, cover what agentic AI marketing is, which tasks agents can take on, how they connect to your CRM, how governance and regulated sectors are handled, how fast a first workflow can go live and how return is measured. In short, agents draft and a named senior person signs off, compliance comes before deployment, and value is judged on pipeline evidence.";
const SERVICE_AUDIENCE = [
  { "@type": "BusinessAudience", "audienceType": "B2B companies" },
  { "@type": "Audience", "audienceType": "B2C and ecommerce brands" }
];
const faqs = [
  {
    q: "What does an agentic AI marketing agency do?",
    a: FAQ_FIRST_ANSWER
  },
  {
    q: "We are already using ChatGPT and Copilot. Why do we need an Agentic AI program?",
    a: "ChatGPT and Copilot can support individual tasks. A bounded agentic AI system connects research, drafting, monitoring and handoffs across an agreed workflow in your existing tools. Your team sets the objective, permitted data and approval boundaries. Agents draft; a named senior person signs off buyer-facing outputs, and no agent spends or publishes alone. The useful distinction is workflow orchestration with accountable review, not unrestricted autonomy."
  },
  {
    q: "Our pipeline is inconsistent and our sales team is wasting time on low-quality leads. Can Agentic AI fix this?",
    a: "Agents can help organize account research and review available buying signals across approved sources, but inconsistent pipeline can also reflect targeting, offer, data quality or sales handoff problems. We start with those questions rather than promising that automation will fix pipeline. Your team agrees qualification criteria, records a baseline and reviews the evidence before an account is routed to sales."
  },
  {
    q: "What marketing tasks can Agentic AI run autonomously?",
    a: "We deploy bounded workflows for market intelligence and competitor monitoring, persona research and ICP enrichment, content drafting, SEO/AEO checks, lead scoring and CRM enrichment, campaign analysis, sales enablement drafts and GTM coordination. Permissions and review requirements are agreed for each task. Agents draft; a named senior person signs off anything buyer-facing, and no agent spends or publishes alone."
  },
  {
    q: "How quickly can we go from decision to deployed agents?",
    a: "Timing depends on the workflow, data access, integrations, security review and approval requirements. A short call helps choose one bounded workflow; we then agree the scope, test criteria and deployment plan. We do not promise a launch date before those dependencies are checked. Production use follows controlled testing and approval by the accountable team."
  },
  {
    q: "Will we need to replace our existing marketing stack?",
    a: "No. Agentic AI integrates with your existing tools: HubSpot, Salesforce, Google Ads, LinkedIn, your CMS, and analytics platforms. The agents operate as an orchestration layer on top of your current stack, making your existing investments work harder rather than replacing them. We design for your stack, not against it."
  },
  {
    q: "What results should we realistically expect?",
    a: "Start with a documented baseline for the task: time to complete, quality checks, qualified demand, lead-to-opportunity movement and attributable pipeline where the data supports it. Agree the comparator, data-quality rule, approval boundary and review date before rollout. Automation can improve throughput or consistency in a defined workflow, but it does not create a universal outcome or remove accountable human oversight."
  },
  {
    q: "How does Agentic AI work alongside our ABM program?",
    a: "Agentic AI can support ABM account research, persona-specific drafts, intent monitoring and engagement scoring within an approved account list. Your team chooses the accounts and buying-group roles, checks the evidence and approves buyer-facing content. The scope follows account value, available data and review capacity, rather than a promised number of simultaneous campaigns. See our Account-Based Marketing service for how we integrate the two."
  },
  {
    q: "What is the difference between deterministic automation and true agentic AI?",
    a: "Deterministic automation follows a fixed rule, such as preparing a follow-up when a form is submitted. Agentic AI can evaluate context, select an action within permitted tools and prepare a response across multiple steps. Both approaches need testing, logs and exception handling. We use fixed rules where precision matters and bounded reasoning where context matters; a named senior person approves buyer-facing output and consequential actions."
  },
  {
    q: "What is agentic AI marketing and how is it different from AI-assisted marketing?",
    a: "AI-assisted marketing usually involves a person prompting a tool for one task. Agentic AI marketing connects specialized agents across an agreed multi-step workflow, such as account research, draft preparation and lead review. The team defines the objective, data access, permissions and measurement plan. Agents can prepare the work, but a named senior person signs off anything buyer-facing, and no agent spends or publishes alone."
  },
  {
    q: "How does an agentic AI system generate leads for B2B companies?",
    a: "A B2B agentic AI lead generation workflow can combine intent monitoring, account research, personalized content drafts and coordinated handoffs. Agents review approved buying signals and prepare evidence for lead qualification against your ICP criteria. Your team checks data quality and approves outreach before it reaches a buyer. This supports lead generation; it does not guarantee that an account is ready to buy or that pipeline will grow."
  },
  {
    q: "Can agentic AI improve our brand's visibility in AI search results like ChatGPT and Perplexity?",
    a: "An agentic content workflow can help a team identify buyer questions, prepare answer-first drafts, check public evidence and monitor observed appearances across selected prompts. It cannot guarantee citation, ranking, recommendation, pipeline or revenue outcomes: answer engines use different retrieval and ranking systems, and changes should be tested against a documented baseline."
  },
  {
    q: "What is the role of GEO (Generative Engine Optimization) in an agentic AI marketing program?",
    a: "GEO (Generative Engine Optimization) helps make content clear and usable for generative AI retrieval. In an agentic AI marketing program, agents can prepare answer-first drafts, check visible-content schema and monitor observed appearances across priority buyer queries. A named reviewer approves evidence and publication. Content changes are tested against a baseline; no workflow can guarantee retrieval, citation, rankings or recommendations."
  },
  {
    q: "How does agentic AI integrate with our existing CRM and marketing automation?",
    a: "Agentic AI can work as an orchestration layer above your existing stack rather than replacing it. We scope API connections to HubSpot, Salesforce, Marketo or your CRM of choice, subject to access and security review. Agents can prepare CRM enrichment, lead scores and proposed sequences within agreed permissions. The team reviews data quality, consequential updates and buyer-facing actions before approval."
  },
  {
    q: "What is the difference between an AI marketing agency and an agentic AI marketing agency?",
    a: "An AI marketing agency may use AI tools to support human-led tasks such as drafting, audience analysis and reporting. An agentic AI marketing agency also connects agents across bounded multi-step workflows in the existing stack. Integrated.Social designs and deploys those systems with documented permissions, measurement and accountable human review. The distinction is coordinated workflow support, not a promise of autonomous pipeline generation or compounding results."
  },
  {
    q: "How do you measure the ROI of an agentic AI marketing program?",
    a: "A useful measurement plan begins with a dated baseline for the workflow being changed: production time, qualified demand, lead-to-opportunity movement, attributable pipeline and, where relevant, observed answer-engine appearances for priority buyer questions. The comparator, attribution window, data-quality rule and human approval boundary should be agreed before rollout. Outcomes vary by use case and are not universal targets."
  },
  {
    q: "Is agentic AI marketing suitable for regulated industries like financial services or healthcare?",
    a: "It can be considered only within a documented governance design. Start with bounded, reviewable tasks; define permitted data and tools; require human approval before consequential publishing or customer-facing action; and involve the organization’s legal, privacy, security and sector specialists. An automated check can support a control, but it does not replace regulatory interpretation or accountable human review."
  },
  {
    q: "Can I try an autonomous AI agent before committing to a full program?",
    a: "Yes. Manus is a third-party AI agent tool (a tool we use, not a partner) that lets you experience multi-step agentic workflows, including research, content creation and data analysis. The invitation link is https://manus.im/invitation/QPAHJBYCPQ3IK?utm_source=invitation&utm_medium=social&utm_campaign=system_share. Check the current tool terms and credit availability before use. It can help you explore a bounded task before scoping a custom agentic program with us."
  },
  {
    q: "Should we deploy the agents before the compliance layer?",
    a: "Not on a regulated account. The sports-media and BetTech example puts a compliance screener on every output before publication, with permissions and a log. A named senior person signs off before anything reaches a buyer; screening does not replace legal review. A draft agent is not a system a legal team can approve. Governance is the deployment condition, not a later tidy-up."
  },
  {
    q: "How should an AI content pipeline handle SEO metadata and publication?",
    a: "Use a bounded workflow that maps each approved page to a descriptive title, meta description, heading structure, canonical URL, internal links, and structured data only when it describes visible content. A named reviewer should approve material claims and the release owner should validate rendered metadata after publication. An agent may prepare and check the package, but it should not publish material claims or high-impact technical changes without approved controls.",
  },
  {
    q: 'What is an "Evidence Gate" in agentic AI marketing?',
    a: 'An Evidence Gate is a required checkpoint between a draft, research finding or proposed action and any production use. It checks that a claim has suitable source context, that the intended action is within the approved scope, and that a named reviewer can approve or reject higher-consequence work. It reduces avoidable errors; it does not make a system infallible.',
  },
  {
    q: 'What is the "Maximum Autonomous Consequence" test?',
    a: 'It asks what commercial, legal, financial or reputational harm an automated workflow could cause before a human can intervene. The answer should set the automation boundary. Research and draft preparation may be appropriate at lower risk, while publishing public claims, changing material spend or contacting sensitive audiences should require explicit human approval.',
  },
  {
    q: "What is agentic AI in marketing?",
    a: "Agentic AI in marketing refers to multi-agent systems that support connected tasks such as ICP research, personalized drafts, test preparation, content production and campaign monitoring. Specialized agents handle defined steps within agreed permissions. Your team sets the objective, checks evidence and reviews outputs; a named senior person signs off anything buyer-facing, and no agent spends or publishes alone."
  },
  {
    q: "How does Integrated.Social build agentic AI systems?",
    a: "We design and deploy multi-agent orchestration systems on Gemini Enterprise, where each agent has a defined role: ICP research, persona-driven message generation, compliance pre-screening, AEO content production or campaign performance monitoring. The architecture is deterministic where precision matters and non-deterministic where context matters. We scope integrations with your existing CRM, ad platforms and content workflows, with permissions, controlled tests and human sign-off."
  },
  {
    q: "What results can agentic AI deliver for B2B pipeline?",
    a: "Agents can take on repeated tasks such as account research, drafting and preparing follow-ups, so your team has more time for buyers. We do not promise a percentage, multiple or date for pipeline growth. We record a baseline before launch, measure the effect on qualified pipeline, and a named senior person signs off every output before it reaches a buyer."
  },
  {
    q: "Is agentic AI suitable for regulated industries?",
    a: "It can be considered only with a documented governance design, approved data access and sector-specific review. A compliance pre-screening agent can check drafts against defined rules, but it does not replace legal interpretation or accountable review. A named senior person signs off before publication or send, and legal, privacy, security and sector specialists should approve the relevant controls before deployment."
  }
];

export default function ServiceAgenticAI() {

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://integrated.social/services/gemini-agentic-ai/#webpage",
        "url": "https://integrated.social/services/gemini-agentic-ai/",
        "name": PAGE_TITLE,
        "headline": "Agentic AI Marketing Agency for B2B Lead Generation",
        "description": PAGE_DESCRIPTION,
        "abstract": ANSWER_BLOCK,
        "dateModified": "2026-11-02",
        "isPartOf": { "@id": "https://integrated.social/#website" },
        "about": { "@id": "https://integrated.social/#organization" },
        "publisher": { "@id": "https://integrated.social/#organization" },
        "breadcrumb": { "@id": "https://integrated.social/services/gemini-agentic-ai/#breadcrumb" },
        "author": { "@id": "https://integrated.social/modi-elnadi/#person" },
        "inLanguage": "en-US"
      },
      {
        "@type": "Service",
        "@id": "https://integrated.social/services/gemini-agentic-ai/#service",
        "name": "Gemini Enterprise Agentic AI for Marketing & Sales",
        "provider": { "@id": "https://integrated.social/#organization" },
        "serviceType": "Agentic AI Marketing System Deployment",
        "description": "Gemini Enterprise multi-agent systems for B2B companies and B2C and ecommerce brands, supporting lead generation, content drafting and agreed GTM tasks with human sign-off.",
        "url": "https://integrated.social/services/gemini-agentic-ai/",
        "audience": SERVICE_AUDIENCE,
        "areaServed": [
          { "@type": "Country", "name": "United Kingdom" },
          { "@type": "Country", "name": "United States" },
          { "@type": "Place", "name": "Global" }
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Agentic AI Services",
          "itemListElement": [
            { "@type": "Offer", "valueAddedTaxIncluded": true, "itemOffered": { "@type": "Service", "name": "Multi-Agent System Architecture & Deployment", "description": "Multi-Agent System Architecture & Deployment for B2B companies and B2C and ecommerce brands, within agreed permissions and human sign-off.", "audience": SERVICE_AUDIENCE } },
            { "@type": "Offer", "valueAddedTaxIncluded": true, "itemOffered": { "@type": "Service", "name": "Marketing Automation with Autonomous AI Agents", "description": "Marketing Automation with Autonomous AI Agents for B2B companies and B2C and ecommerce brands, within agreed permissions and human sign-off.", "audience": SERVICE_AUDIENCE } },
            { "@type": "Offer", "valueAddedTaxIncluded": true, "itemOffered": { "@type": "Service", "name": "Sales Enablement & CRM Enrichment Agents", "description": "Sales Enablement & CRM Enrichment Agents for B2B companies and B2C and ecommerce brands, within agreed permissions and human sign-off.", "audience": SERVICE_AUDIENCE } },
            { "@type": "Offer", "valueAddedTaxIncluded": true, "itemOffered": { "@type": "Service", "name": "GTM Strategy Deployment at Scale", "description": "GTM Strategy Deployment at Scale for B2B companies and B2C and ecommerce brands, within agreed permissions and human sign-off.", "audience": SERVICE_AUDIENCE } }
          ]
        }
      },
      {
        "@type": "FAQPage",
        "@id": "https://integrated.social/services/gemini-agentic-ai/#faq",
        "isPartOf": { "@id": "https://integrated.social/services/gemini-agentic-ai/#webpage" },
        "mainEntity": faqs.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a }
        }))
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://integrated.social/services/gemini-agentic-ai/#breadcrumb",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://integrated.social/" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://integrated.social/services/" },
          { "@type": "ListItem", "position": 3, "name": "Agentic AI Marketing Agency London", "item": "https://integrated.social/services/gemini-agentic-ai/" }
        ]
      }
    ]
  };

  return (
    <>
      <SEOHead
        title={PAGE_TITLE}
        ogImage="https://integrated.social/media/iso-agentic-service_40547b1b.png"
        description={PAGE_DESCRIPTION}
        locale="en-US"
        alternates={[
          { hrefLang: "en-US", href: "https://integrated.social/services/gemini-agentic-ai/" },
          { hrefLang: "ar", href: "https://integrated.social/ar/services/gemini-agentic-ai/" },
          { hrefLang: "x-default", href: "https://integrated.social/services/gemini-agentic-ai/" }
        ]}
        canonical="https://integrated.social/services/gemini-agentic-ai/"
      />
      <SchemaScript schema={JSON.stringify(schemaData)} />
      <div className="min-h-screen bg-background">
        <Header />
        <main>
        {/* Breadcrumb */}
        <nav className="container pt-24 pb-4" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-muted-foreground">
            <li><Link href="/" className="hover:text-foreground transition-colors">Home</Link></li>
            <li>/</li>
            <li><Link href="/services/" className="hover:text-foreground transition-colors">Services</Link></li>
            <li>/</li>
            <li className="text-foreground font-medium">Gemini Enterprise Agentic AI</li>
          </ol>
        </nav>

        {/* Hero */}
        <header className="container pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="max-w-4xl">
            <span className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-widest text-[#FF8C00] border border-[#FF8C00]/30 rounded-full mb-6">
              Could AI agents support your team’s repeated marketing tasks?
            </span>
            <h1 className="font-serif headline-xl mb-6">
              Agentic AI Marketing Agency for B2B Lead Generation
            </h1>
            <p className="subheading text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed mb-3">Stop scaling headcount. Start deploying AI agents.</p>
            <p data-answer-block className="text-base text-muted-foreground max-w-3xl leading-relaxed mb-6">{ANSWER_BLOCK}</p>
            <p className="text-base text-muted-foreground max-w-3xl leading-relaxed mb-6">
              We build and deploy Gemini Enterprise multi-agent environments that support lead generation, content drafting and agreed GTM tasks. Your team sets direction; agents draft; a named senior person signs off before anything reaches a buyer.
            </p>
            <p className="text-base text-muted-foreground max-w-3xl leading-relaxed mb-6">We support B2B companies and B2C and ecommerce brands, with scope, permissions and review requirements agreed for each workflow.</p>
            {/* RTB strip */}
            <div className="grid grid-cols-2 gap-3 mb-6 max-w-xl">
              <div className="border border-[#FF8C00]/30 bg-[#FF8C00]/5 rounded-sm px-3 py-2 text-center">
                <div className="text-[#FF8C00] font-bold text-base">+312%</div>
                <div className="text-muted-foreground text-xs mt-0.5">Qualified pipeline in 90 days · B2B SaaS (ABM only)</div>
              </div>
              <div className="border border-[#00D4AA]/30 bg-[#00D4AA]/5 rounded-sm px-3 py-2 text-center">
                <div className="text-[#00D4AA] font-bold text-base">£2.4M</div>
                <div className="text-muted-foreground text-xs mt-0.5">Pipeline in 6 months · B2B SaaS</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London" href="https://calendly.com/modielnadi/30min" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF8C00] text-white font-medium rounded-lg hover:bg-[#E07B00] transition-all duration-200 active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_agentic_ai"); trackSectionCtaClick({ section_name: "service_agentic_ai_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}>
                Book your free Agentic AI Review <ArrowRight className="w-4 h-4" />
              </a>
              <Link href="/blog/ai-agents-replacing-b2b-research-phase/"
                className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-medium rounded-lg hover:bg-accent transition-all duration-200">
                Read: How AI agents replace B2B research
              </Link>
            </div>
          </div>
          <div className="hidden lg:block">
            <img
              src="/media/iso-agentic-service-1200_b8002eaa.webp"
              srcSet="/media/iso-agentic-service-800_c9f1a3fe.webp 800w, /media/iso-agentic-service-1200_b8002eaa.webp 1200w"
              sizes="(max-width: 1024px) 100vw, 50vw"
              alt="Multi-agent marketing workflow illustration showing research, drafting and coordinated handoffs"
              className="w-full h-auto rounded-xl"
              width={1200}
              height={800}
              loading="lazy"
              decoding="async"
            />
          </div>
          </div>
        </header>

        {/* What We Deploy */}
        <section className="bg-[#1a1a1a] text-white py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-4">What AI agents we deploy</h2>
            <p className="text-gray-400 text-lg max-w-2xl mb-12">
              Bounded AI workflows for research, drafting and monitoring, with set permissions, a documented baseline and accountable human review.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { icon: Bot, title: "Market Intelligence Agents", desc: "Agents that monitor approved competitor and industry sources and prepare findings for your team to review." },
                { icon: Users, title: "Persona Research & ICP Enrichment", desc: "Agents that build and continuously refine ideal customer profiles, enrich CRM data, and score leads based on behavioral signals and firmographic fit." },
                { icon: Zap, title: "Content Production at Scale", desc: "Multi-agent workflows that research and draft blogs, social posts, email sequences and sales collateral. A named senior person checks evidence and brand voice before publication." },
                { icon: Target, title: "Lead Generation & Pipeline Building", desc: "Agents that research target accounts, prepare personalized outreach and propose lead handoffs across approved channels, subject to qualification and human sign-off." },
                { icon: BarChart3, title: "PPC & Performance Optimization", desc: "Campaign analysis, creative test preparation and negative keyword suggestions across Google Ads and Performance Max. Spend changes require explicit human approval." },
                { icon: Workflow, title: "GTM Strategy Execution", desc: "Full go-to-market orchestration: from positioning and messaging to channel deployment and measurement. Strategies that ship, not decks that gather dust." }
              ].map((item, i) => (
                <div key={i} className="p-6 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                  <item.icon className="w-8 h-8 text-[#FF8C00] mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* Audio Narrative - voice summary of this service page */}
        <div className="container max-w-4xl mx-auto px-4 py-6">
          <AudioPlayer
            text={ANSWER_BLOCK}
            title="Agentic AI for B2B Marketing and Sales"
            readingTime={5}
          />
        </div>

        <section className="py-16 bg-[#0f0f0f] text-white">
          <div className="container">
            <h2 className="font-serif headline-lg mb-4">How we assess deployed workflows</h2>
            <p className="text-gray-400 text-lg max-w-3xl">The multi-agent architecture reflects the enterprise GTM system deployed for a regulated B2B client. We agree a baseline and review evidence for each workflow; there is no universal pipeline or conversion target.</p>
          </div>
        </section>

        {/* Commercial KPIs */}
        <section className="py-16 bg-[#0F1419]">
          <div className="container">
            <span className="text-mono text-[#FF8C00] block mb-3 text-xs uppercase tracking-widest">Commercial KPIs</span>
            <h2 className="font-serif text-2xl md:text-3xl text-white mb-2">How we measure Agentic AI marketing</h2>
            <p className="text-white/60 text-base max-w-2xl mb-8">We agree a measurement plan for each deployed workflow, including qualified demand and attributable pipeline where the data supports it, not just impressions or activity metrics.</p>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              {[
                { metric: "Leads Generated", description: "Qualified leads produced by deployed agentic systems, tracked by persona and source" },
                { metric: "Pipeline Value", description: "Qualified pipeline attributed to the agreed workflow where data supports it, with weekly reporting" },
              ].map((kpi, i) => (
                <div key={i} className="flex items-start gap-4 p-5 border border-white/10 rounded-sm hover:border-[#FF8C00]/30 transition-colors">
                  <span className="text-[#FF8C00] text-2xl font-serif leading-none mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="text-white font-semibold text-base mb-1">{kpi.metric}</h3>
                    <p className="text-white/50 text-sm">{kpi.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <a href="/#kpis" className="text-[#FF8C00] text-sm font-medium hover:underline">← See all 6 AI marketing KPIs on the homepage</a>
          </div>
        </section>

        <section className="py-16 bg-[#111827] text-white">
          <div className="container">
            <h2 className="font-serif text-2xl md:text-3xl mb-3">The five baselines set at the start</h2>
            <p className="text-white/60 mb-8 max-w-3xl">The page names the units; it does not print a universal target.</p>
            <div className="overflow-x-auto border border-white/10 rounded-lg">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-white/5"><tr><th className="px-4 py-3 text-left">Baseline</th><th className="px-4 py-3 text-left">Unit the page names</th><th className="px-4 py-3 text-left">Number</th></tr></thead>
                <tbody className="divide-y divide-white/10 text-white/60">
                  {[
                    ['Content production velocity', 'Pieces per week'],
                    ['Qualified leads', 'Per month'],
                    ['Lead-to-opportunity conversion rate', 'Rate'],
                    ['Pipeline value attributed to AI-driven campaigns', 'Pipeline value'],
                    ['AI citation rate', 'Rate for priority buyer queries'],
                  ].map(([baseline, unit]) => <tr key={baseline}><td className="px-4 py-3 text-white/90">{baseline}</td><td className="px-4 py-3">{unit}</td><td className="px-4 py-3">Set at the start of the engagement. Not printed as a universal target.</td></tr>)}
                </tbody>
              </table>
            </div>
            <p className="mt-6 text-sm text-white/60">For regulated work, <Link href="/services/ai-governance/" className="text-[#FF8C00] hover:underline">AI governance</Link> is the deployment condition. The content agent described here works within <Link href="/services/seo-aeo-geo/" className="text-[#FF8C00] hover:underline">SEO, AEO and GEO</Link>.</p>
          </div>
        </section>

        {/* The Agentic Difference */}
        <section className="py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-12">The AI agent difference</h2>
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-red-500">Traditional AI tools (ChatGPT, Copilot)</h3>
                <ul className="space-y-3">
                  {[
                    "One prompt → one response → wait for human",
                    "No memory between sessions",
                    "Cannot access your systems or data",
                    "Requires constant human supervision",
                    "Output quality depends on prompt quality",
                    "Cannot execute multi-step workflows"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <span className="text-red-500 mt-1">✗</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4 text-[#FF8C00]">Agentic AI agents (what we deploy)</h3>
                <ul className="space-y-3">
                  {[
                    "Autonomous: observe → plan → execute → iterate",
                    "Persistent memory and learning from results",
                    "Integrated with your CRM, CMS, ads, and analytics",
                    "Works within set permissions; a named senior person signs off its output",
                    "Changes reviewed against performance data and approval rules",
                    "Coordinates specialist agents within the approved workflow"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-foreground">
                      <CheckCircle2 className="w-5 h-5 text-[#FF8C00] mt-0.5 shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Deterministic vs Non-Deterministic */}
            <div className="mt-16 border border-border rounded-2xl overflow-hidden">
              <div className="bg-[#FF8C00]/10 border-b border-border px-8 py-5">
                <p className="text-xs font-mono uppercase tracking-widest text-[#FF8C00] mb-1">The deeper distinction</p>
                <h3 className="font-serif text-2xl md:text-3xl">Deterministic vs non-deterministic: why it matters</h3>
              </div>
              <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center text-red-500 font-bold text-sm">D</span>
                    <h4 className="text-lg font-semibold text-red-400">Deterministic automation</h4>
                  </div>
                  <p className="text-muted-foreground leading-relaxed mb-5">Every output is pre-defined. Step A always produces output B. The system follows a fixed script - which means it is only as good as the person who wrote the script, and only works for the scenarios that person anticipated.</p>
                  <ul className="space-y-2.5 text-sm text-muted-foreground">
                    {[
                      "If X then Y - no deviation, no judgment",
                      "Breaks silently on edge cases and novel scenarios",
                      "Requires manual updates whenever conditions change",
                      "Scales volume, not intelligence",
                      "What most agencies call 'agentic AI'"
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-red-500 mt-0.5 shrink-0">✗</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="w-8 h-8 rounded-full bg-[#FF8C00]/10 flex items-center justify-center text-[#FF8C00] font-bold text-sm">N</span>
                    <h4 className="text-lg font-semibold text-[#FF8C00]">Non-deterministic agentic AI</h4>
                  </div>
                  <p className="text-muted-foreground leading-relaxed mb-5">The agent reasons about the situation, selects the appropriate action from a trained decision space, and adapts its approach based on context and outcome signals. The output is not pre-written - it is derived. This is where genuine intelligence lives.</p>
                  <ul className="space-y-2.5 text-sm text-muted-foreground">
                    {[
                      "Evaluates context before deciding on action",
                      "Handles novel scenarios within trained guardrails",
                      "Hands off to the right specialist agent dynamically",
                      "Learns from outcomes and adjusts future decisions",
                      "Review improvements against the agreed baseline, not a promised date"
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#FF8C00] mt-0.5 shrink-0" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="bg-accent/40 border-t border-border px-8 py-5">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <span className="text-foreground font-medium">The critical insight:</span> deterministic systems scale effort. Non-deterministic agentic systems scale judgment. The difference between an agency running automation workflows and one running true agentic AI is not the tools - it is whether the system can reason, adapt, and improve without being reprogrammed every time the market moves.
                </p>
                <Link
                  href="/blog/true-agentic-ai-vs-automation-workflows-methodology-2026/"
                  className="inline-flex items-center gap-2 mt-4 text-sm text-[#FF8C00] font-semibold hover:underline"
                >
                  Read: True Agentic AI vs Automation Workflows - why most agencies are getting it wrong <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* How We Work */}
        <section className="bg-accent/50 py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-12">How we deploy your AI agent system</h2>
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Audit & AI Agent Architecture", desc: "We map your current marketing operations, identify where AI agents can replace manual effort, and design the multi-agent architecture - which AI agents, what data flows, which integrations." },
                { step: "02", title: "AI Agent Development", desc: "We build specialist AI agents for each function: research, content, outreach, optimization. Each AI agent is trained on your brand voice, ICP data, and performance benchmarks." },
                { step: "03", title: "Integration & Testing", desc: "AI agents connect to your existing stack - CRM, CMS, Google Ads, LinkedIn, analytics. We run controlled tests to validate output quality and measure against human baselines." },
                { step: "04", title: "Deploy & Optimize", desc: "AI agents go live in production. We monitor performance, tune decision logic, and scale successful workflows." }
              ].map((item, i) => (
                <div key={i} className="relative">
                  <span className="text-6xl font-serif text-[#FF8C00] opacity-40">{item.step}</span>
                  <h3 className="text-lg font-semibold mt-2 mb-3">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>



        {/* How we price */}
        <section className="py-20 bg-[#0d0d0d] text-white">
          <div className="container">
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-widest text-[#FF8C00] font-semibold">Engagement model</span>
              <h2 className="font-serif text-3xl md:text-4xl mt-3 mb-6">How we price: pay-per-resolution, not per seat</h2>
              <p className="text-lg text-gray-300 mb-6">
                Agentic AI should be priced on what it resolves, not what it licenses. Integrated.Social structures every Agentic AI engagement around qualified outputs: leads generated, pipeline influenced, content assets shipped, and GTM actions executed. You pay for results, not for agent seats or hours logged.
              </p>
              <p className="text-gray-400 mb-8">
                Every deployment starts with a defined success metric agreed before we write a single line of agent logic. We measure against it weekly. If the system is not hitting its resolution targets, we fix the architecture, not the invoice.
              </p>
              <div className="grid sm:grid-cols-3 gap-6 mb-8">
                <div className="border border-[#FF8C00]/30 rounded-lg p-5">
                  <div className="text-[#FF8C00] font-bold text-lg mb-1">Pay-per-resolution</div>
                  <div className="text-gray-300 text-sm">Priced per qualified lead, content asset, or GTM action delivered, not per agent seat.</div>
                </div>
                <div className="border border-[#FF8C00]/30 rounded-lg p-5">
                  <div className="text-[#FF8C00] font-bold text-lg mb-1">Outcome-based</div>
                  <div className="text-gray-300 text-sm">Fees tied to pipeline contribution and agreed KPIs, reviewed every sprint.</div>
                </div>
                <div className="border border-[#FF8C00]/30 rounded-lg p-5">
                  <div className="text-[#FF8C00] font-bold text-lg mb-1">No seat tax</div>
                  <div className="text-gray-300 text-sm">Scale your agent fleet without scaling your license bill. You own the output, not the overhead.</div>
                </div>
              </div>
              <Link
                href="/blog/outcome-based-ai-pricing-b2b-marketing-agentforce-2026/"
                className="inline-flex items-center gap-2 text-[#FF8C00] hover:underline text-sm font-medium"
              >
                Why outcome-based AI pricing is the new standard for B2B marketing <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
        {/* Pillar-Spoke Links */}
        <section className="bg-[#1a1a1a] text-white py-16">
          <div className="container">
            <h2 className="font-serif text-2xl md:text-3xl mb-8">Related services &amp; reading</h2>

            <Link href="/blog/openai-gpt56-sol-ipo-b2b-marketing-strategy-2026/" className="group block border border-[#FF8C00]/30 rounded-xl p-7 mb-6 hover:border-[#FF8C00]/60 transition-colors">
              <p className="promo-title font-serif text-xl md:text-2xl font-bold text-white mb-3">Related reading: OpenAI and B2B marketing strategy</p>
              <p className="text-sm text-gray-400 leading-relaxed">Explore questions for B2B teams evaluating changes in AI tools and buyer research.</p>
              <span className="inline-flex items-center gap-2 mt-4 text-sm text-[#FF8C00] font-semibold">Read the analysis →</span>
            </Link>

            {/* Secondary featured article: AI Agents replacing B2B research phase */}
            <Link
              href="/blog/ai-agents-replacing-b2b-research-phase/"
              className="group block border border-white/10 rounded-xl p-6 mb-8 hover:border-[#FF8C00]/30 transition-colors relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-1 h-full bg-[#FF8C00]/50 rounded-l-xl" />
              <div className="pl-2">
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-gray-400 border border-white/20 rounded-full">
                    Agentic AI
                  </span>
                  <span className="text-xs text-gray-500">Published June 27, 2026</span>
                </div>
                <h3 className="font-serif text-lg font-bold text-white group-hover:text-[#FF8C00] transition-colors leading-snug mb-2">
                  How AI Agents Are Replacing the B2B Research Phase Before Vendors Are Contacted
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-3 max-w-3xl">
                  Explore how AI-assisted buyer research changes the evidence and content a B2B team should prepare before a sales conversation.
                </p>
                <span className="inline-flex items-center gap-2 text-sm text-[#FF8C00]/80 font-semibold">
                  Read the analysis →
                </span>
              </div>
            </Link>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Link href="/services/seo-aeo-geo/" className="block p-5 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                <span className="text-xs uppercase tracking-wider text-[#FF8C00]">Service</span>
                <h3 className="text-lg font-semibold mt-2">AI Search Optimization (SEO + AEO/GEO)</h3>
                <p className="text-gray-400 text-sm mt-2">Prepare clear, evidenced content for traditional search and AI retrieval.</p>
              </Link>
              <Link href="/services/ppc-performance-max/" className="block p-5 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                <span className="text-xs uppercase tracking-wider text-[#FF8C00]">Service</span>
                <h3 className="text-lg font-semibold mt-2">PPC & Performance Max</h3>
                <p className="text-gray-400 text-sm mt-2">Scale Google Ads with structured A/B testing and ROAS discipline.</p>
              </Link>
              <Link href="/services/ai-marketing-strategy/" className="block p-5 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                <span className="text-xs uppercase tracking-wider text-[#FF8C00]">Service</span>
                <h3 className="text-lg font-semibold mt-2">AI Marketing Strategy & Content Ops</h3>
                <p className="text-gray-400 text-sm mt-2">Persona-driven GTM with deployed content systems.</p>
              </Link>
              <Link href="/services/google-ai-mode-optimisation/" className="block p-5 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                <span className="text-xs uppercase tracking-wider text-[#FF8C00]">Service</span>
                <h3 className="text-lg font-semibold mt-2">Google AI Mode Optimization</h3>
                <p className="text-gray-400 text-sm mt-2">Structure content to appear in Google's AI Mode conversational answers and multi-step reasoning results.</p>
              </Link>
              <Link href="/services/preferred-sources-strategy/" className="block p-5 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                <span className="text-xs uppercase tracking-wider text-[#FF8C00]">Service</span>
                <h3 className="text-lg font-semibold mt-2">Preferred Sources Strategy</h3>
                <p className="text-gray-400 text-sm mt-2">Develop evidence and content for the questions your buyers ask, and monitor observed answer-engine appearances.</p>
              </Link>
              <Link href="/blog/aeo-2026-complete-guide-answer-engine-optimisation-b2b/" className="block p-5 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                <span className="text-xs uppercase tracking-wider text-gray-500">Article</span>
                <h3 className="text-lg font-semibold mt-2">AEO in 2026: Complete Guide</h3>
                <p className="text-gray-400 text-sm mt-2">How to structure content for AI answer citations.</p>
              </Link>
              <Link href="/blog/gemini-enterprise-agent-platform-replaces-vertex-ai/" className="block p-5 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                <span className="text-xs uppercase tracking-wider text-gray-500">Article</span>
                <h3 className="text-lg font-semibold mt-2">Gemini Enterprise Replaces Vertex AI</h3>
                <p className="text-gray-400 text-sm mt-2">Why this platform change matters for B2B marketing.</p>
              </Link>
              <Link href="/blog/chatgpt-workspace-agents-b2b-gtm-dark-funnel-2026/" className="block p-5 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                <span className="text-xs uppercase tracking-wider text-gray-500">Article</span>
                <h3 className="text-lg font-semibold mt-2">ChatGPT Workspace Agents and the B2B Dark Funnel</h3>
                <p className="text-gray-400 text-sm mt-2">How AI agents are rewriting B2B GTM before the first sales call.</p>
              </Link>
              <Link href="/blog/zero-click-commerce-b2b-ai-agents-2026/" className="block p-5 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                <span className="text-xs uppercase tracking-wider text-gray-500">Article</span>
                <h3 className="text-lg font-semibold mt-2">Zero-Click Commerce: B2B AI Agents Bypass the Buyer Journey</h3>
                <p className="text-gray-400 text-sm mt-2">What your marketing team must do before AI agents make the purchase decision.</p>
              </Link>
              <Link href="/blog/salesforce-agentforce-pay-per-resolution-enterprise-ai-pricing-2026/" className="block p-5 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                <span className="text-xs uppercase tracking-wider text-gray-500">Article</span>
                <h3 className="text-lg font-semibold mt-2">Salesforce Agentforce Pay-Per-Resolution Pricing</h3>
                <p className="text-gray-400 text-sm mt-2">Every CMO needs to understand what this pricing shift means for enterprise AI budgets.</p>
              </Link>
              <Link href="/blog/bcg-cmo-survey-2026-agentic-marketing-transformation/" className="block p-5 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                <span className="text-xs uppercase tracking-wider text-gray-500">Article</span>
                <h3 className="text-lg font-semibold mt-2">BCG CMO Survey 2026: The Agentic Marketing Gap</h3>
                <p className="text-gray-400 text-sm mt-2">Questions for marketing leaders evaluating governed agentic workflows and organizational readiness.</p>
              </Link>
              <Link href="/blog/preferred-source-chatgpt-gemini-perplexity/" className="block p-5 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                <span className="text-xs uppercase tracking-wider text-gray-500">Article</span>
                <h3 className="text-lg font-semibold mt-2">How to Become a Preferred Source in ChatGPT, Gemini, and Perplexity</h3>
                <p className="text-gray-400 text-sm mt-2">Technical and editorial considerations for clear content, supported claims and observed answer-engine visibility.</p>
              </Link>
              <Link href="/about/" className="block p-5 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                <span className="text-xs uppercase tracking-wider text-gray-500">About</span>
                <h3 className="text-lg font-semibold mt-2">Modi Elnadi, 20+ Years in Digital Marketing and AI</h3>
                <p className="text-gray-400 text-sm mt-2">The person behind the deployed systems.</p>
              </Link>
            </div>
          </div>
        </section>

        {/* See it in action, Agentic AI GTM Case Study */}
        <section className="py-20 bg-[#0D1220]">
          <div className="container">
            <div className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/30 rounded-full px-4 py-1.5 text-teal-400 text-xs font-semibold tracking-widest uppercase mb-8">
              See It In Action
            </div>
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Copy */}
              <div>
                <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
                  One Orchestration Engine: Five Personas, Three Languages, Human Sign-off
                </h2>
                <p className="text-slate-300 leading-relaxed mb-4">
                  A regulated sports media and BetTech infrastructure provider needed to break into
                  three new European markets simultaneously. Without a marketing team large enough
                  to produce persona-specific GTM content at that scale or speed.
                </p>
                <p className="text-slate-300 leading-relaxed mb-4">
                  We designed and deployed a multi-agent orchestration system on Gemini Enterprise Plus.
                  Each agent had a defined role: ICP researcher, persona writer, compliance screener,
                  translation coordinator, CRM enricher, and sales deck assembler. The orchestrator
                  routed tasks, enforced compliance guardrails, and ensured every output was
                  calibrated to one of five buying personas, CEO/MD, Commercial Director,
                  Product/Technical Lead, Media/Growth Head, and Compliance/Legal Officer.
                </p>
                <p className="text-slate-300 leading-relaxed mb-6">
                  The system prepared persona-specific assets in English, Spanish and Portuguese.
                  Agents draft; a named senior person signs off before anything reaches a buyer,
                  with legal review where the sector requires it.
                </p>
                <blockquote className="border-l-4 border-teal-500 pl-5 text-slate-400 italic mb-8">
                  "The orchestration layer is what makes it real. Any agent can draft content -
                  but only a system that understands persona hierarchy, compliance constraints,
                  and language nuance can produce assets you can actually put in front of a
                  regulated industry buyer."
                  <span className="block mt-2 text-teal-400 not-italic text-sm font-semibold">- Modi Elnadi, Integrated.Social</span>
                </blockquote>
                <Link
                  href="/case-studies/agentic-ai-gtm/"
                  className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-black font-bold px-6 py-3 rounded-xl text-sm transition-all hover:scale-105 active:scale-95"
                >
                  Read the full case study <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              {/* Result cards */}
              <div className="space-y-4">
                {[
                  { metric: "5", label: "Buying personas served", sub: "CEO/MD · Commercial · Product/Tech · Media/Growth · Compliance/Legal" },
                  { metric: "3", label: "Languages at launch", sub: "English · Spanish · Portuguese" },
                ].map((r) => (
                  <div key={r.metric} className="bg-white/[0.04] border border-teal-500/20 rounded-xl p-5 flex items-start gap-5">
                    <div className="stat-number text-3xl text-teal-400 min-w-[3.5rem]">{r.metric}</div>
                    <div>
                      <div className="text-white font-semibold text-sm">{r.label}</div>
                      <div className="text-slate-500 text-xs mt-0.5">{r.sub}</div>
                    </div>
                  </div>
                ))}
                <div className="bg-teal-500/5 border border-teal-500/20 rounded-xl p-5">
                  <div className="text-xs font-mono text-teal-500/60 uppercase tracking-widest mb-2">Sector</div>
                  <div className="text-white text-sm font-semibold">Sports Media & BetTech Infrastructure · Regulated Industry · Europe</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* First 30 Days */}
        <section id="first-30-days" className="py-16 bg-[#F7F5F2]">
          <div className="container">
            <span className="font-mono text-[#FF8C00] block mb-3 text-xs uppercase tracking-widest">Initial workflow scope</span>
            <h2 className="font-serif text-2xl md:text-3xl text-[#1A1A2E] mb-2">Your initial agentic AI marketing workflow</h2>
            <p className="text-[#6B7280] text-base max-w-2xl mb-8">We start by understanding your brand, buyers and existing tools. We agree one bounded workflow, its data access, approval rules and baseline before testing. Deployment scope and timing depend on integrations, governance review and evidence from the controlled test.</p>
            <div className="bg-white border border-[#E8E4DF] rounded-sm p-8">
              <h3 className="font-mono text-[#6B7280] text-xs uppercase tracking-widest mb-5">Deliverables</h3>
              <ul className="space-y-3">
                {[
                  "Brand and persona deep-dive: understanding your USPs, buying committee, and what makes your buyers say yes",
                  "Bounded lead generation workflow: research and draft preparation, controlled testing and human-approved handoffs",
                  "AI Search visibility audit: where you appear (and don't) in AI answers, with a fix-first priority list",
                  "PPC/PMax restructure: persona-segmented campaigns, A/B testing framework, and budget reallocation to pipeline",
                  "Content campaign preparation: persona-driven SEO drafts, LinkedIn sequences and email drafts for human approval",
                  "Initial A/B tests running: ad copy, landing pages, and messaging variants with clear hypotheses and success metrics",
                  "Agreed GTM deployment plan tied to qualified demand and attributable pipeline, with weekly review cycles",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" />
                    <span className="text-[#4B5563] text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-[#E8E4DF] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-[#6B7280] text-sm">Systems that reflect your brand truth, with clear measurement of their effect.</p>
                <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London" href="https://calendly.com/modielnadi/30min" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#FF8C00] text-white px-5 py-2.5 text-sm font-semibold rounded-sm hover:bg-[#E67E00] transition-colors shrink-0" onClick={() => { trackCalendlyClick("service_agentic_ai"); trackSectionCtaClick({ section_name: "service_agentic_ai_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}>
                  Discuss my first workflow →
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Visibility Engineering */}
        <section id="visibility-engineering" className="py-16 bg-white border-t border-[#E8E4DF]">
          <div className="container">
            <div className="grid lg:grid-cols-5 gap-12 items-start">
              <div className="lg:col-span-3">
                <span className="font-mono text-[#FF8C00] block mb-3 text-xs uppercase tracking-widest">Visibility Engineering + Agentic AI</span>
                <h2 className="font-serif text-2xl md:text-3xl text-[#1A1A2E] mb-4">AI Marketing Visibility: the new commercial SEO powered by AI agents</h2>
                <div className="space-y-4 text-[#4B5563] text-base leading-relaxed">
                  <p><strong>Visibility Engineering</strong> is the practice of making your brand easy for AI systems to retrieve, trust, and reference. It combines entity clarity, answer-first content structure, and technical hygiene so your pages become citable in AI Overviews and AI Mode, while still converting high-intent clicks into leads.</p>
                  <p>We accelerate this with <strong>Agentic AI lead generation</strong>. Our deployed Gemini Enterprise systems run daily, producing citation-ready content tailored to each persona's buying stage, monitoring competitor visibility, running A/B tested outreach sequences, and generating leads through persona-segmented campaigns.</p>
                  <p>We combine <strong>Answer Engine Optimization (AEO)</strong>, <strong>Generative Engine Optimization (GEO)</strong>, and traditional SEO into one unified system powered by agentic workflows. Your content is structured for both traditional search rankings and AI-generated citations.</p>
                </div>
                <a href="/services/seo-aeo-geo/" className="inline-flex items-center gap-2 mt-6 text-[#FF8C00] text-sm font-medium hover:underline">
                  See our full SEO, AEO &amp; GEO service →
                </a>
              </div>
              <div className="lg:col-span-2">
                <div className="bg-[#0F1419] rounded-sm p-6">
                  <h3 className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">Structured Data Coverage</h3>
                  <ul className="space-y-3">
                    {[
                      { label: "Brand & entity identity", desc: "Establishes who you are across AI knowledge graphs" },
                      { label: "Service definitions", desc: "Maps each offering to machine-readable intent" },
                      { label: "Answer & Q&A blocks", desc: "Structures content for direct AI extraction" },
                      { label: "Author & expertise signals", desc: "Supports E-E-A-T and knowledge graph inclusion" },
                      { label: "Voice & AI citation targets", desc: "Marks priority passages for multimodal retrieval" },
                    ].map(({ label, desc }) => (
                      <li key={label} className="flex items-start gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FF8C00] mt-1.5 shrink-0" />
                        <div>
                          <span className="text-white/80 text-sm">{label}</span>
                          <p className="text-white/40 text-xs mt-0.5">{desc}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-20">
          <div className="container max-w-3xl">
            <h2 className="font-serif text-3xl md:text-4xl mb-8">Frequently asked questions</h2>
            <p data-faq-summary className="text-muted-foreground leading-relaxed mb-8">{FAQ_SUMMARY}</p>
            <div className="space-y-4">
              <FaqAccordion
                  items={faqs.slice(0, -4)}
                  headingLevel={3}
                  defaultOpenIndex={0}
                  className="space-y-4"
                  itemClassName="border border-border rounded-lg overflow-hidden"
                  buttonClassName="w-full flex items-center justify-between p-5 text-left hover:bg-accent/50 transition-colors"
                  questionClassName="m-0"
                  answerClassName="px-5 pb-5 text-muted-foreground leading-relaxed"
                  iconClassName="w-5 h-5 shrink-0 transition-transform duration-200"
                />
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#FF8C00] py-16">
          <div className="container text-center">
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">Ready to deploy agentic AI for your marketing?</h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Book a 30-minute call to choose one bounded workflow, review relevant examples and agree its permissions, measurement and human sign-off requirements.
            </p>
            <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London" href="https://calendly.com/modielnadi/30min" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#1a1a1a] font-semibold rounded-lg hover:bg-gray-100 transition-all duration-200 active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_agentic_ai"); trackSectionCtaClick({ section_name: "service_agentic_ai_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}>
              Book your 30-minute AI Growth Review <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>

        {/* AEO Answer Block: structured Q&A for direct AI extraction */}
        <section id="aeo-answers" className="py-16 bg-white border-t border-[#E8E4DF]" aria-labelledby="agentic-ai-answers-heading">
          <div className="container">
            <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-3">Quick answers</span>
            <h2 id="agentic-ai-answers-heading" className="font-serif text-2xl lg:text-3xl text-[#1A1A2E] mb-10 max-w-2xl leading-snug">
              Agentic AI for B2B marketing: common questions
            </h2>
            <div className="grid md:grid-cols-2 gap-x-12 gap-y-8">
              {faqs.slice(-4).map(faq => (
                <div key={faq.q}>
                  <h3 className="font-semibold text-[#1A1A2E] text-base mb-2">{faq.q}</h3>
                  <p className="text-[#6B7280] text-sm leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA - three ways to work with us */}
        <section id="get-started" className="py-20 lg:py-28 bg-[#0F1419]" aria-labelledby="service-final-cta-heading">
          <div className="container">
            <div className="text-center mb-14">
              <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Ready to start?</span>
              <h2 id="service-final-cta-heading" className="font-serif text-3xl md:text-4xl text-white mb-4">
                Three ways to work with us
              </h2>
              <p className="text-white/50 text-lg max-w-xl mx-auto">
                Pick the path that fits where you are right now.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">Free · No obligation</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Audit your domain for AI Answers</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  See exactly where your brand appears in Google AI Mode, ChatGPT, Perplexity, and Gemini. Free AI Visibility Score with a scored report and gap analysis.
                </p>
                <a aria-label="Check my AI visibility"
                  href="/free-ai-growth-audit/"
                  className="inline-flex items-center justify-center gap-2 bg-[#FF8C00] text-[#0F1419] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#E07B00] transition-colors active:scale-[0.97]" onClick={() => trackSectionCtaClick({ section_name: "service_agentic_ai_bottom_cta", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
                >
                  Check my AI visibility<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">1-to-1 · Personalized</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">AI Executive Training for Leaders</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  Three sessions built around your role, your sector, and your goals. Hands-on AI training that makes you dangerous with the tools your competitors are still figuring out.
                </p>
                <a
                  href="/services/ai-training-for-leaders/"
                  className="inline-flex items-center justify-center gap-2 border border-[#FF8C00] text-[#FF8C00] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#FF8C00] hover:text-[#0F1419] transition-colors active:scale-[0.97]"
                >
                  Explore AI Training
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">30 min · No pitch</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Discuss your AI marketing strategy</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  A focused conversation about where you are, where you want to be, and what a realistic 90-day AI marketing deployment looks like for your business. No deck. No sales script.
                </p>
                <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                  href="https://calendly.com/modielnadi/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/80 px-6 py-3.5 text-sm font-bold rounded-sm hover:border-[#FF8C00] hover:text-[#FF8C00] transition-colors active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_agentic_ai"); trackSectionCtaClick({ section_name: "service_agentic_ai_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
                >
                  Schedule a discovery call
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
            </div>
          </div>
        </section>
        </main>
      </div>
      {/* Arabic-aware share row */}
      <div className="container max-w-5xl mx-auto px-4 pb-16">
        <SharePostRow
          title="Gemini Agentic AI Marketing Systems"
          canonicalUrl="https://integrated.social/services/gemini-agentic-ai/"
          excerpt={PAGE_DESCRIPTION}
        />
      </div>

      {/* Arabic translation toggle */}
      <ArabicToggle />
    </>
  );
}
